import type { FC, PropsWithChildren } from 'react';
import { createContext, memo, useCallback, useContext, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { SalesPromotionDto } from '@/services/ws-funnel/src/main/resources/rest-api';
import { getSalesPromotionById } from '@/services/ws-funnel/src/main/resources/rest-api';
import { Loader } from '@/js/libs/ui';
import type { OnSuccess } from '@/js/libs/use-request-legacy';
import { useRequestLegacy } from '@/js/libs/use-request-legacy';

import ErrorComponent from 'src/components/Error';
import { useRequestErrorNotification } from 'src/hooks/useRequestErrorNotification';
import type { PromotionDetailsRouteParams } from './types';

interface Context {
  promotion: SalesPromotionDto;
  isLoadingPromotion: boolean;
  reloadPromotion: () => void;
  setPromotion: (promotion: SalesPromotionDto) => void;
}

const PromotionContext = createContext<Context | null>(null);

const PromotionContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { promotionId: salesPromotionId } = useParams<PromotionDetailsRouteParams>();

  const [promotion, setPromotion] = useState<SalesPromotionDto | undefined>(undefined);

  const [, { pending, error }, reloadPromotion] = useRequestLegacy({
    apiMethod: getSalesPromotionById,
    parameters: [{ salesPromotionId }],
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-restricted-syntax
    onSuccess: useCallback<OnSuccess<getSalesPromotionById>>((response) => setPromotion(response), []),
    onFail: useRequestErrorNotification(),
  });

  const contextValue = useMemo<Context | null>(() => {
    if (!promotion) {
      return null;
    }

    return {
      isLoadingPromotion: pending,
      promotion,
      reloadPromotion,
      setPromotion,
    };
  }, [pending, promotion, reloadPromotion]);

  if (pending) {
    return <Loader />;
  }

  if (error) {
    return <ErrorComponent title={error?.message} />;
  }

  return <PromotionContext.Provider value={contextValue}>{children}</PromotionContext.Provider>;
};

export const usePromotionContext = (): Context => {
  const value = useContext(PromotionContext);

  if (value === null) {
    throw new Error('`usePromotionContext` is used outside of `PromotionContextProvider`');
  }

  return value;
};

export default memo<typeof PromotionContextProvider>(PromotionContextProvider);
